// subparts/TimetableEntryCard.tsx
import React, { useState, useEffect } from 'react';
import { GlobalChipSelector, ChipOption } from '../../../../components/GlobalComponents/GlobalChipSelector';
import { GlobalCheckboxes, CheckboxOption } from '../../../../components/GlobalComponents/GlobalCheckboxes';
import { GlobalInputField } from '../../../../components/GlobalComponents/GlobalInputField';
import apiClient from '../../../../stateManagers/services/axiosInterceptor';
import { hasNoSubject, hasOptionalTeacher, formatSlotTypeLabel } from './timetableSlotTypes';

interface TimetableEntry {
  id: string;
  timeSlotId: string;
  subjectId: string;
  teacherAssignmentId: string;
  daysOfWeek: string[];
  roomNumber: string;
}

interface TimetableEntryCardProps {
  entry: TimetableEntry;
  index: number;
  classId: string;
  section: string | null;
  canRemove: boolean;
  timeSlotOptions: ChipOption[];
  subjectOptions: ChipOption[];
  timeSlotsLoading: boolean;
  subjectsLoading: boolean;
  timeSlotsError?: string | null;
  subjectsError?: string | null;
  canShowEntries: boolean;
  loading: boolean;
  occupiedDays?: string[];
  slotType?: string | null;
  onUpdate: (
    id: string,
    field: 'timeSlotId' | 'subjectId' | 'teacherAssignmentId' | 'roomNumber',
    value: string
  ) => void;
  onUpdateDays: (id: string, days: string[]) => void;
  onRemove: (id: string) => void;
}

// Stores both the assignment id and the actual teacher_id.
// For optional-teacher slots (no subject), value === teacherId since there's no assignment row.
interface TeacherOption extends ChipOption {
  teacherId: string;
}

const TimetableEntryCard: React.FC<TimetableEntryCardProps> = ({
  entry,
  index,
  classId,
  section,
  canRemove,
  timeSlotOptions,
  subjectOptions,
  timeSlotsLoading,
  subjectsLoading,
  timeSlotsError = null,
  subjectsError = null,
  canShowEntries,
  loading,
  occupiedDays = [],
  slotType = null,
  onUpdate,
  onUpdateDays,
  onRemove,
}) => {
  const [teacherOptions, setTeacherOptions] = useState<TeacherOption[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);

  const noSubject = hasNoSubject(slotType);
  const optionalTeacher = hasOptionalTeacher(slotType);
  // Strict non-teaching: no subject AND no teacher allowed (i.e. 'break')
  const noTeacherAtAll = noSubject && !optionalTeacher;

  // No time slots exist at all for this timetable type — the rest of the
  // entry (subject/teacher/days/room) is meaningless without one, so lock it down.
  const noTimeSlotsAvailable = canShowEntries && !timeSlotsLoading && timeSlotOptions.length === 0;

  const dayOptions: CheckboxOption[] = [
    { value: 'Monday',    label: 'Monday',    disabled: occupiedDays.includes('Monday')    },
    { value: 'Tuesday',   label: 'Tuesday',   disabled: occupiedDays.includes('Tuesday')   },
    { value: 'Wednesday', label: 'Wednesday', disabled: occupiedDays.includes('Wednesday') },
    { value: 'Thursday',  label: 'Thursday',  disabled: occupiedDays.includes('Thursday')  },
    { value: 'Friday',    label: 'Friday',    disabled: occupiedDays.includes('Friday')    },
    { value: 'Saturday',  label: 'Saturday',  disabled: occupiedDays.includes('Saturday')  },
  ];

  useEffect(() => {
    const fetchTeachers = async () => {
      // No time slots at all, or strict non-teaching slot (break): no teacher dropdown
      if (noTimeSlotsAvailable || noTeacherAtAll) {
        setTeacherOptions([]);
        return;
      }

      // Optional-teacher slots (power_nap / workbook / activities): fetch ALL teachers, no subject scoping
      if (optionalTeacher) {
        setTeachersLoading(true);
        try {
          const response = await apiClient.get('teachers-list2', {
            params: { page: 1, limit: 100 },
          });

          if (response.data.status === 'success') {
            const list = response.data.data ?? [];
            const options: TeacherOption[] = list
              .map((t: any) => ({
                value: t.id.toString(),
                label: t.name,
                teacherId: t.id.toString(),
              }))
              .sort((a: TeacherOption, b: TeacherOption) => a.label.localeCompare(b.label));

            setTeacherOptions(options);
          } else {
            setTeacherOptions([]);
          }
        } catch (error) {
          console.error('Error fetching teachers list:', error);
          setTeacherOptions([]);
        } finally {
          setTeachersLoading(false);
        }
        return;
      }

      // Normal teaching slot: subject-scoped assigned teachers
      if (!entry.subjectId || !classId) {
        setTeacherOptions([]);
        return;
      }

      setTeachersLoading(true);
      try {
        const sectionParam = section ?? '';
        const endpoint = `subjects-assigned-teachers-filters/${classId}/section/${sectionParam}/subject/${entry.subjectId}`;
        const response = await apiClient.get(endpoint);

        if (response.data.status === 'success') {
          const teachersArray = response.data.assignments ?? response.data.data ?? [];

          const options: TeacherOption[] = teachersArray
            .map((item: any) => ({
              // value = assignment row id (used as key in chip)
              value: item.id.toString(),
              label: `${item.teacher_name} (${item.subject_name})`,
              // teacherId = actual teacher_id for the API payload
              teacherId: item.teacher_id.toString(),
            }))
            .sort((a: TeacherOption, b: TeacherOption) =>
              a.label.localeCompare(b.label)
            );

          setTeacherOptions(options);
        } else {
          setTeacherOptions([]);
        }
      } catch (error) {
        console.error('Error fetching teachers:', error);
        setTeacherOptions([]);
      } finally {
        setTeachersLoading(false);
      }
    };

    fetchTeachers();
  }, [entry.subjectId, classId, section, noTimeSlotsAvailable, noTeacherAtAll, optionalTeacher]);

  // When chip selection changes, store id AND notify parent with real teacher_id
  // (for optionalTeacher slots, value === teacherId directly; for normal slots, value is the assignment id)
  const handleTeacherChange = (selectedValue: string) => {
    if (!selectedValue) {
      onUpdate(entry.id, 'teacherAssignmentId', '');
      return;
    }
    const selected = teacherOptions.find((t) => t.value === selectedValue);
    if (selected) {
      onUpdate(entry.id, 'teacherAssignmentId', selected.teacherId);
    }
  };

  // Reverse-map stored teacher_id back to chip display value
  const chipDisplayValue =
    entry.teacherAssignmentId
      ? teacherOptions.find((t) => t.teacherId === entry.teacherAssignmentId)?.value ?? ''
      : '';

  const showTeacherSelector = !noTimeSlotsAvailable && (optionalTeacher || (!noSubject && !!entry.subjectId));

  /* ── Empty-state messages, derived from real state — not guessed ── */

  const timeSlotEmptyMessage = !canShowEntries
    ? 'Complete previous fields first'
    : timeSlotsError
      ? timeSlotsError
      : 'No time slots configured for this timetable type';

  const subjectEmptyMessage = !classId
    ? 'Select class first'
    : subjectsError
      ? subjectsError
      : 'No subjects available for this class';

  const teacherEmptyMessage = (!optionalTeacher && !entry.subjectId)
    ? 'Select subject first'
    : optionalTeacher
      ? 'No teachers found'
      : 'No teachers assigned for this subject';

  return (
    <div className="entry-card">
      <div className="entry-number">Entry {index + 1}</div>

      {canRemove && (
        <button
          className="remove-button"
          onClick={() => onRemove(entry.id)}
          disabled={loading}
        >
          <i className="pi pi-times" style={{ fontSize: '11px' }} />
        </button>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '8px' }}>
        <GlobalChipSelector
          label="Time Slot"
          icon="pi pi-clock"
          value={entry.timeSlotId}
          onChange={(v) => onUpdate(entry.id, 'timeSlotId', v)}
          options={timeSlotOptions}
          required
          loading={timeSlotsLoading}
          disabled={loading || !canShowEntries}
          emptyMessage={timeSlotEmptyMessage}
        />

        {noTimeSlotsAvailable && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            No time slots are configured for this timetable type — add one before filling out the rest of this entry
          </div>
        )}

        {!noSubject && (
          <GlobalChipSelector
            label="Subject"
            icon="pi pi-book"
            value={entry.subjectId}
            onChange={(v) => {
              onUpdate(entry.id, 'subjectId', v);
              // Clear teacher when subject changes — teacher fetch re-runs via useEffect
              onUpdate(entry.id, 'teacherAssignmentId', '');
            }}
            options={subjectOptions}
            required
            loading={subjectsLoading}
            disabled={loading || !classId || noTimeSlotsAvailable}
            emptyMessage={subjectEmptyMessage}
          />
        )}

        {noTeacherAtAll && !noTimeSlotsAvailable && (
          <div className="info-note">
            <i className="pi pi-info-circle" />
            This is a {formatSlotTypeLabel(slotType || '')} slot — no subject or teacher needed
          </div>
        )}

        {showTeacherSelector && (
          <GlobalChipSelector
            label={optionalTeacher ? 'Supervising Teacher (optional)' : 'Assigned Teacher'}
            icon="pi pi-user"
            value={chipDisplayValue}
            onChange={handleTeacherChange}
            options={teacherOptions}
            required={!optionalTeacher}
            loading={teachersLoading}
            disabled={loading || (teacherOptions.length === 0 && !optionalTeacher)}
            allowDeselect
            emptyMessage={teacherEmptyMessage}
          />
        )}

        <GlobalCheckboxes
          label="Days of Week"
          options={dayOptions}
          value={entry.daysOfWeek}
          onChange={(days) => onUpdateDays(entry.id, days)}
          required
          layout="horizontal"
          gap={16}
          disabled={loading || noTimeSlotsAvailable}
        />

        <GlobalInputField
          label="Room Number"
          value={entry.roomNumber}
          onChange={(e) => onUpdate(entry.id, 'roomNumber', e.target.value)}
          placeholder="e.g. 101, Lab-3, Auditorium"
          disabled={loading || noTimeSlotsAvailable}
          icon="pi pi-home"
        />
      </div>
    </div>
  );
};

export default TimetableEntryCard;